import { useCallback, useEffect, useState } from 'react';
import { useResourceDefinitions, useStoreContext } from 'ra-core';
import { useLocation } from 'react-router-dom';
import { CURRENT_CUSTOMER_CHANGED_EVENT } from '../lib/currentCustomer';

/**
 * Lists whose selection store key is not the URL path (Resource name).
 * Path-based useUnselectAll(location.pathname) misses these.
 */
const SELECTION_RESOURCE_ALIASES = [
  'all-assets',
  'lite-templates',
  'specials/bhv-knop/device-list',
];

const selectedIdsStoreKey = (resource: string) => `${resource}.selectedIds`;

/** Build store keys for every known list selection (definitions + localStorage leftovers). */
export const collectSelectedIdsStoreKeys = (
  resourceNames: string[],
  localStorageKeys: Array<string | null>,
): string[] => {

  const resources = new Set(resourceNames.filter(Boolean));

  for (const fullKey of localStorageKeys) {

    if (!fullKey?.startsWith('RaStore') || !fullKey.endsWith('.selectedIds')) {

      continue;

    }
    // RaStore[.appKey].<resource>.selectedIds
    const match = fullKey.match(/^RaStore[^.]*\.(.+\.selectedIds)$/);
    if (match?.[1]) {

      resources.add(match[1].replace(/\.selectedIds$/, ''));

    }

  }

  return [...resources].map(selectedIdsStoreKey);

};

export const useAutoUnselector = () => {

  const location = useLocation();
  const store = useStoreContext();
  const definitions = useResourceDefinitions();
  const [lastPath, setLastPath] = useState<string | null>(null);

  const clearAllListSelections = useCallback(() => {

    const keys = collectSelectedIdsStoreKeys(
      [...Object.keys(definitions ?? {}), ...SELECTION_RESOURCE_ALIASES],
      Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i)),
    );

    for (const key of keys) {

      store.removeItem(key);

    }

  }, [definitions, store]);

  // Clear when navigating between pages (previous path had a selection).
  useEffect(() => {

    const path = location.pathname.slice(1);

    if (path !== lastPath) {

      if (lastPath) {

        clearAllListSelections();

      }
      setLastPath(path);

    }

  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear when the top-bar customer changes (path-based unselectAll is unreliable).
  useEffect(() => {

    window.addEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, clearAllListSelections);
    return () => window.removeEventListener(CURRENT_CUSTOMER_CHANGED_EVENT, clearAllListSelections);

  }, [clearAllListSelections]);

};
